<template>
  <div class="cost-center-allocation-editor">
    <!-- Allocations Table -->
    <div class="allocations-table mb-4">
      <v-simple-table dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th>{{ $tc('cost-center.cost-center') }}</th>
              <th class="text-center">{{ $t('cost-center.before') }}</th>
              <th class="text-center">{{ $t('cost-center.after') }}</th>
            </tr>
          </thead>
          <tbody>
            <!-- Empty state when no cost centers -->
            <tr v-if="localAllocations.length === 0" class="empty-state-row">
              <td colspan="3" class="text-center grey--text py-4">
                {{ $t('cost-center.no-allocations') }}
              </td>
            </tr>

            <!-- Cost center rows -->
            <tr
              v-for="(alloc, index) in localAllocations"
              :key="index"
              :class="{ 'deleted-allocation': alloc.isDeleted }"
            >
              <td>
                <div class="d-flex align-center">
                  <span :class="{ 'text-decoration-line-through': alloc.isDeleted }">
                    {{ alloc.costCenterName }}
                  </span>
                  <v-chip v-if="alloc.isNew && !alloc.isDeleted" x-small color="success" class="ml-2">
                    {{ $t('new') }}
                  </v-chip>
                  <v-chip v-if="alloc.isDeleted" x-small color="error" class="ml-2">
                    {{ $t('cost-center.deleted') }}
                  </v-chip>
                  <v-btn
                    icon
                    x-small
                    class="ml-2"
                    @click="toggleDeleteAllocation(index)"
                  >
                    <v-icon v-if="!alloc.isDeleted" x-small color="error">mdi-close</v-icon>
                    <v-icon v-else x-small color="success">mdi-undo</v-icon>
                  </v-btn>
                </div>
              </td>
              <td class="text-center">{{ alloc.beforePercent.toFixed(1) }}%</td>
              <td class="text-center">
                <v-text-field
                  v-model.number="alloc.sharePercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  suffix="%"
                  dense
                  hide-details
                  outlined
                  :disabled="alloc.isDeleted"
                  @input="onPercentChange(alloc)"
                />
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <!-- Add Cost Center -->
      <v-btn
        v-if="availableCostCenters.length > 0"
        small
        text
        block
        class="mt-2"
        @click="openAddCostCenter"
      >
        <v-icon left small>mdi-plus</v-icon>
        {{ $t('cost-center.add-cost-center') }}
      </v-btn>
    </div>

    <!-- Quick Actions (only if multiple active cost centers) -->
    <div v-if="activeAllocationsCount >= 2" class="quick-actions mb-4">
      <v-btn small outlined block class="mb-2" @click="distributeEqually">
        <v-icon left small>mdi-chart-pie</v-icon>
        {{ $t('cost-center.distribute-equally') }}
      </v-btn>
      <v-btn small outlined block class="mb-2" @click="keepRatios">
        <v-icon left small>mdi-chart-line</v-icon>
        {{ $t('cost-center.keep-ratios') }}
      </v-btn>
      <v-btn
        v-if="hasZeroAllocations"
        small
        outlined
        block
        :disabled="!canCompleteZeros"
        @click="completeZeros"
      >
        <v-icon left small>mdi-percent</v-icon>
        {{ $t('cost-center.complete-zeros') }}
      </v-btn>
    </div>

    <!-- Visual Progress Bar (only if multiple active cost centers) -->
    <div v-if="activeAllocationsCount >= 2" class="visual-progress mb-4">
      <div class="progress-label mb-1">
        {{ $t('cost-center.total') }} :
        <span :class="totalClass">{{ totalPercent.toFixed(2) }}%</span>
        <v-icon v-if="isValid" small color="success">mdi-check-circle</v-icon>
        <v-icon v-else small color="warning">mdi-alert-circle</v-icon>
      </div>

      <v-progress-linear
        :value="Math.min(totalPercent, 100)"
        height="20"
        :color="progressColor"
        striped
      >
        <template v-slot:default>
          <strong class="white--text">{{ totalPercent.toFixed(1) }}%</strong>
        </template>
      </v-progress-linear>
    </div>

    <!-- Validation Messages (only if multiple active cost centers) -->
    <v-alert v-if="activeAllocationsCount >= 2 && !isValid" type="warning" dense text class="mb-4">
      {{ validationMessage }}
    </v-alert>

    <!-- Add Cost Center Dialog -->
    <v-dialog v-model="showAddCostCenter" max-width="400">
      <v-card>
        <v-card-title>{{ $t('cost-center.add-cost-center') }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="selectedCostCenterId"
            :items="availableCostCenters"
            item-text="name"
            item-value="id"
            :label="$t('cost-center.select-cost-center')"
            outlined
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="showAddCostCenter = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            small
            :disabled="!selectedCostCenterId"
            @click="addCostCenter"
          >
            {{ $t('add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'CostCenterAllocationEditor',

  props: {
    // v-model: Array of {costCenterId, costCenterName, sharePercent}
    value: {
      type: Array,
      default: () => []
    },
    // Pre-loaded cost centers list (REQUIRED to avoid multiple API calls)
    costCenters: {
      type: Array,
      required: true
    },
    // Organization ID
    organizationId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      localAllocations: [],
      showAddCostCenter: false,
      selectedCostCenterId: null,
      totalPercent: 0,
      isValid: false,
      validationMessage: '',
      isUpdatingFromProp: false
    }
  },

  computed: {
    availableCostCenters() {
      // Exclude cost centers already in the list (not deleted)
      const currentCCIds = this.localAllocations
        .filter(a => !a.isDeleted)
        .map(a => a.costCenterId)
      return this.costCenters.filter(cc => !currentCCIds.includes(cc.id))
    },

    hasInitialCostCenters() {
      // Check if entity had cost centers initially
      return this.localAllocations.some(a => a.beforePercent > 0)
    },

    activeAllocationsCount() {
      // Count non-deleted allocations
      return this.localAllocations.filter(a => !a.isDeleted).length
    },

    hasZeroAllocations() {
      const activeAllocations = this.localAllocations.filter(a => !a.isDeleted)
      return activeAllocations.some(a => a.sharePercent === 0 || !a.sharePercent)
    },

    availableSpaceForZeros() {
      const activeAllocations = this.localAllocations.filter(a => !a.isDeleted)
      const nonZeros = activeAllocations.filter(a => a.sharePercent > 0)
      const usedSpace = nonZeros.reduce((sum, a) => sum + a.sharePercent, 0)
      return Math.round((100 - usedSpace) * 100) / 100
    },

    canCompleteZeros() {
      return this.hasZeroAllocations && this.availableSpaceForZeros > 0
    },

    totalClass() {
      return this.isValid ? 'success--text' : 'warning--text'
    },

    progressColor() {
      if (this.isValid) return 'success'
      if (this.totalPercent > 100) return 'error'
      return 'warning'
    }
  },

  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        // Prevent infinite loop by setting flag
        this.isUpdatingFromProp = true

        // Normalize allocations from backend format (id, name) to frontend format (costCenterId, costCenterName)
        // and add beforePercent tracking
        if (newValue && newValue.length > 0) {
          this.localAllocations = newValue.map(allocation => {
            // If allocation already has beforePercent and isDeleted (from internal state), preserve them
            // Otherwise, initialize them (first load from backend)
            const hasInternalState = 'beforePercent' in allocation && 'isDeleted' in allocation

            return {
              costCenterId: allocation.costCenterId || allocation.id,
              costCenterName: allocation.costCenterName || allocation.name,
              beforePercent: hasInternalState ? allocation.beforePercent : parseFloat(allocation.sharePercent || 0),
              sharePercent: parseFloat(allocation.sharePercent || 0),
              isDeleted: hasInternalState ? allocation.isDeleted : false,
              isNew: allocation.isNew || false
            }
          })
        } else {
          this.localAllocations = []
        }

        // Reset flag after DOM update
        this.$nextTick(() => {
          this.isUpdatingFromProp = false
          this.validateTotal()
        })
      }
    },

    localAllocations: {
      deep: true,
      handler() {
        // Emit all allocations (including soft-deleted ones) via v-model
        // The modal will filter out deleted ones when saving
        if (!this.isUpdatingFromProp) {
          this.$emit('input', this.localAllocations)
          this.$emit('validation', {
            valid: this.isValid,
            errors: this.isValid ? [] : [this.validationMessage]
          })
        }
      }
    }
  },

  methods: {
    onPercentChange(alloc) {
      // Round to 2 decimals
      alloc.sharePercent = Math.round(alloc.sharePercent * 100) / 100
      this.validateTotal()
    },

    validateTotal() {
      // Filter out deleted cost centers for validation
      const activeAllocations = this.localAllocations.filter(a => !a.isDeleted)

      // If no active cost centers
      if (activeAllocations.length === 0) {
        this.totalPercent = 0
        this.isValid = true // Always valid to have no cost centers
        this.validationMessage = ''
        return
      }

      // Calculate total
      this.totalPercent = activeAllocations.reduce((sum, alloc) => {
        return sum + (parseFloat(alloc.sharePercent) || 0)
      }, 0)

      // Round total to 2 decimals
      this.totalPercent = Math.round(this.totalPercent * 100) / 100

      // If only one active cost center, it's always valid (regardless of percentage)
      if (activeAllocations.length === 1) {
        this.isValid = true
        this.validationMessage = ''
        return
      }

      // For multiple cost centers: check if total is 100%
      const diff = Math.abs(this.totalPercent - 100)
      this.isValid = diff <= 0.001

      // Generate validation message
      if (diff > 0.001) {
        if (this.totalPercent < 100) {
          this.validationMessage = this.$t('cost-center.total-below-100', {
            diff: (100 - this.totalPercent).toFixed(2)
          })
        } else {
          this.validationMessage = this.$t('cost-center.total-above-100', {
            diff: (this.totalPercent - 100).toFixed(2)
          })
        }
      } else {
        this.validationMessage = ''
      }

      // Check for 0% allocations (only on active allocations with multiple cost centers)
      const hasZero = activeAllocations.some(a => a.sharePercent === 0 || !a.sharePercent)
      if (hasZero) {
        this.isValid = false
        this.validationMessage = this.$t('cost-center.zero-percent-forbidden')
      }
    },

    distributeEqually() {
      const activeAllocations = this.localAllocations.filter(a => !a.isDeleted)
      const count = activeAllocations.length
      if (count === 0) return

      const equalShare = Math.round((100 / count) * 100) / 100

      // Assign equal share to all
      activeAllocations.forEach(alloc => {
        alloc.sharePercent = equalShare
      })

      // Calculate the total after rounding
      const total = activeAllocations.reduce((sum, a) => sum + a.sharePercent, 0)

      // Adjust the last cost center to make exactly 100%
      if (activeAllocations.length > 0) {
        const diff = 100 - total
        activeAllocations[activeAllocations.length - 1].sharePercent =
          Math.round((activeAllocations[activeAllocations.length - 1].sharePercent + diff) * 100) / 100
      }

      this.validateTotal()
    },

    keepRatios() {
      const activeAllocations = this.localAllocations.filter(a => !a.isDeleted)
      if (this.totalPercent === 0) return

      const factor = 100 / this.totalPercent
      activeAllocations.forEach(alloc => {
        alloc.sharePercent = Math.round((alloc.sharePercent * factor) * 100) / 100
      })

      // Calculate the total after rounding
      const total = activeAllocations.reduce((sum, a) => sum + a.sharePercent, 0)

      // Adjust the last cost center to make exactly 100%
      if (activeAllocations.length > 0) {
        const diff = 100 - total
        activeAllocations[activeAllocations.length - 1].sharePercent =
          Math.round((activeAllocations[activeAllocations.length - 1].sharePercent + diff) * 100) / 100
      }

      this.validateTotal()
    },

    completeZeros() {
      const activeAllocations = this.localAllocations.filter(a => !a.isDeleted)

      // Separate zeros and non-zeros
      const zeros = activeAllocations.filter(a => a.sharePercent === 0 || !a.sharePercent)
      const nonZeros = activeAllocations.filter(a => a.sharePercent > 0)

      if (zeros.length === 0) return

      // Calculate available space
      const usedSpace = nonZeros.reduce((sum, a) => sum + a.sharePercent, 0)
      const availableSpace = 100 - usedSpace

      if (availableSpace <= 0) return // Safety check (button should be disabled)

      // Distribute available space equally among zeros
      const sharePerZero = availableSpace / zeros.length

      zeros.forEach(alloc => {
        alloc.sharePercent = Math.round(sharePerZero * 100) / 100
      })

      // Adjust the last zero to reach exactly 100%
      const newTotal = activeAllocations.reduce((sum, a) => sum + a.sharePercent, 0)
      if (zeros.length > 0) {
        const diff = 100 - newTotal
        zeros[zeros.length - 1].sharePercent =
          Math.round((zeros[zeros.length - 1].sharePercent + diff) * 100) / 100
      }

      this.validateTotal()
    },

    toggleDeleteAllocation(index) {
      const alloc = this.localAllocations[index]

      // Always use soft delete (never hard delete)
      // This allows user to undo if they made a mistake
      alloc.isDeleted = !alloc.isDeleted

      // If we're undeleting, reset the sharePercent to beforePercent
      if (!alloc.isDeleted) {
        alloc.sharePercent = alloc.beforePercent
      }

      this.validateTotal()
    },

    openAddCostCenter() {
      // Pre-select the first available cost center
      if (this.availableCostCenters.length > 0) {
        this.selectedCostCenterId = this.availableCostCenters[0].id
      }
      this.showAddCostCenter = true
    },

    addCostCenter() {
      if (!this.selectedCostCenterId) return

      const cc = this.costCenters.find(c => c.id === this.selectedCostCenterId)
      if (!cc) return

      // Check if this cost center already exists in the list (even if soft-deleted)
      const existingAllocation = this.localAllocations.find(
        a => a.costCenterId === this.selectedCostCenterId
      )

      if (existingAllocation) {
        // Cost center already exists (probably soft-deleted), restore it instead of creating duplicate
        if (existingAllocation.isDeleted) {
          existingAllocation.isDeleted = false
          // Restore to its beforePercent value
          existingAllocation.sharePercent = existingAllocation.beforePercent
        }
      } else {
        // Cost center doesn't exist yet, add it as new
        const hadNoCostCenters = this.localAllocations.length === 0

        this.localAllocations.push({
          costCenterId: cc.id,
          costCenterName: cc.name,
          beforePercent: 0,
          sharePercent: 0,
          isNew: true,
          isDeleted: false
        })

        // Smart auto-distribution
        if (hadNoCostCenters) {
          // Entity had no cost center: assign 100% to the new one
          this.localAllocations[0].sharePercent = 100
        }
      }

      this.selectedCostCenterId = null
      this.showAddCostCenter = false
      this.validateTotal()
    }
  }
}
</script>

<style scoped>
.cost-center-allocation-editor {
  width: 100%;
}

.allocations-table >>> table th {
  font-size: 12px;
  font-weight: 600;
}

.allocations-table >>> table td {
  font-size: 13px;
}

.allocations-table >>> table input {
  font-size: 13px;
}

.deleted-allocation {
  opacity: 0.6;
  background-color: #ffebee;
}

.deleted-allocation td {
  color: #c62828;
}

.quick-actions {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
}

.visual-progress .progress-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}
</style>
